import {
  IAuthProvider,
  AuthProviderName,
  AuthResult,
  AuthUser,
  UserProfile,
  SignUpResult,
} from "./types";
import FirebaseAuthProvider from "./providers/firebase/FirebaseAuthProvider";
import AwsAuthProvider from "./providers/aws/AwsAuthProvider";
import CloudflareAuthProvider from "./providers/cloudflare/CloudflareAuthProvider";

function createProvider(name: AuthProviderName): IAuthProvider {
  switch (name) {
    case "firebase":
      return new FirebaseAuthProvider();
    case "aws":
      return new AwsAuthProvider();
    case "cloudflare":
      return new CloudflareAuthProvider();
    default:
      throw new Error(`Unknown auth provider: ${name}`);
  }
}

const providerName =
  (process.env.REACT_APP_AUTH_PROVIDER as AuthProviderName) ?? "firebase";

const provider = createProvider(providerName);

const authService = {
  signIn: (email: string, password: string): Promise<AuthResult> =>
    provider.signIn(email, password),

  signOut: (): Promise<void> => provider.signOut(),

  signUp: (email: string, password: string): Promise<SignUpResult> =>
    provider.signUp(email, password),

  getCurrentUser: (): AuthUser | null => provider.getCurrentUser(),

  onAuthStateChanged: (
    callback: (user: AuthUser | null) => void,
  ): (() => void) => provider.onAuthStateChanged(callback),

  getEmailByTreasuryId: (treasuryId: string): Promise<string | null> =>
    provider.getEmailByTreasuryId(treasuryId),

  getUserProfile: (uid: string): Promise<UserProfile> =>
    provider.getUserProfile(uid),

  getMustChangePassword: (uid: string): Promise<boolean> =>
    provider.getMustChangePassword(uid),

  reauthenticate: (currentPassword: string): Promise<void> =>
    provider.reauthenticate(currentPassword),

  sendPasswordReset: (email: string, continueUrl?: string): Promise<void> =>
    provider.sendPasswordReset(email, continueUrl),

  changePassword: (newPassword: string): Promise<void> =>
    provider.changePassword(newPassword),

  signInWithCustomToken: (
    token: string,
    tenantId?: string | null,
  ): Promise<AuthResult> => provider.signInWithCustomToken(token, tenantId),

  setPassword: (password: string): Promise<void> =>
    provider.setPassword(password),

  getToken: (): Promise<string | null> => provider.getToken(),
};

export default authService;
